/**
 * Shape key layer classification.
 *
 * Mannequin GLB models contain morph targets organized into semantic layers:
 *
 * - **proportion**    — `prop_*` (e.g., `prop_chest_pos`, `prop_thighs_neg`)
 * - **corrective**    — `corr_*` (pose-dependent corrections)
 *
 * This module provides the canonical layer enum, classification function,
 * and display metadata — no UI, no state, no side effects.
 */

// ---------------------------------------------------------------------------
// Layer enum
// ---------------------------------------------------------------------------

export const SHAPE_KEY_LAYERS = ["proportion", "corrective"] as const;

export type ShapeKeyLayer = (typeof SHAPE_KEY_LAYERS)[number];

// ---------------------------------------------------------------------------
// Display metadata
// ---------------------------------------------------------------------------

export const SHAPE_KEY_LAYER_LABELS: Record<ShapeKeyLayer, string> = {
  proportion: "Proportions",
  corrective: "Corrective",
};

// ---------------------------------------------------------------------------
// Classification
// ---------------------------------------------------------------------------

const PROP_RE = /^prop_/;
const CORR_RE = /^corr_/;

/**
 * Classify a morph target name into its layer, or `null` if it doesn't
 * belong to any recognised layer (e.g. artist-authored custom shape keys).
 */
export function classifyMorph(morphName: string): ShapeKeyLayer | null {
  if (PROP_RE.test(morphName)) return "proportion";
  if (CORR_RE.test(morphName)) return "corrective";
  return null;
}

/**
 * Build a layer → morph-index set for a given morph target dictionary.
 * Useful for efficiently zero-ing out an entire layer at runtime.
 */
export function indexMorphsByLayer(
  morphTargetDictionary: Record<string, number> | undefined,
): Map<ShapeKeyLayer, Set<number>> {
  const map = new Map<ShapeKeyLayer, Set<number>>();
  if (!morphTargetDictionary) return map;

  for (const [name, index] of Object.entries(morphTargetDictionary)) {
    const layer = classifyMorph(name);
    if (!layer) continue;
    let set = map.get(layer);
    if (!set) {
      set = new Set();
      map.set(layer, set);
    }
    set.add(index);
  }

  return map;
}
